import QRCode from 'qrcode';

const PRMD_GOLD = {
  light: '#FBE7A8',
  mid: '#E6BE63',
  deep: '#B8841F',
};

/**
 * Builds a small, cached "sand" texture tile: a warm speckled noise used as a
 * soft-light overlay so the gold modules read like brushed/sand gold rather
 * than flat fill. Generated once and reused across renders.
 */
let sandTile: HTMLCanvasElement | null = null;
function getSandTile(): HTMLCanvasElement {
  if (sandTile) return sandTile;
  const tile = document.createElement('canvas');
  tile.width = 96;
  tile.height = 96;
  const tctx = tile.getContext('2d')!;
  const img = tctx.createImageData(tile.width, tile.height);
  for (let i = 0; i < img.data.length; i += 4) {
    // Warm speckle: random luminance biased light, low alpha applied at draw.
    const n = Math.random();
    const v = 150 + Math.floor(n * 105); // 150..255
    img.data[i] = v; // r
    img.data[i + 1] = Math.floor(v * 0.86); // g (warm)
    img.data[i + 2] = Math.floor(v * 0.5); // b (gold-ish)
    img.data[i + 3] = 255;
  }
  tctx.putImageData(img, 0, 0);
  sandTile = tile;
  return tile;
}

/** Rounded-rectangle path helper. */
function roundRect(
  ctx: CanvasRenderingContext2D | Path2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/**
 * Loads the centre logo once and caches the decoded image, so the QR can be
 * drawn in a single pass with the pyramid already baked in. Returns the cached
 * image synchronously when available.
 */
export const logoCache = new Map<string, HTMLImageElement>();
const logoPending = new Map<string, Promise<HTMLImageElement | null>>();
export function loadLogo(src: string): Promise<HTMLImageElement | null> {
  const cached = logoCache.get(src);
  if (cached) return Promise.resolve(cached);
  let pending = logoPending.get(src);
  if (!pending) {
    pending = new Promise<HTMLImageElement | null>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        logoCache.set(src, img);
        resolve(img);
      };
      img.onerror = () => resolve(null);
      img.src = src;
    });
    logoPending.set(src, pending);
  }
  return pending;
}
// Warm the default logo immediately so the first QR reveal paints in one pass.
void loadLogo('/prmd-logo.webp');

/**
 * Draws the full Pyramida-styled QR (background, gold modules, sand texture and
 * the centre logo pad/ring) onto `canvas` at an exact `px` square resolution.
 * `logoImg` is the already-decoded centre logo (or undefined to draw just the
 * pad). Uses error-correction level H (~30% recoverable) so the centre logo
 * never breaks scanning. Kept resolution-independent so the same routine backs
 * both the on-screen canvas and high-resolution PNG exports for print.
 */
export function paintStyledQr(
  canvas: HTMLCanvasElement,
  value: string,
  px: number,
  logoImg: HTMLImageElement | undefined,
) {
  const qr = QRCode.create(value, { errorCorrectionLevel: 'H' });
  const count = qr.modules.size;
  const data = qr.modules.data;

  const margin = 4; // quiet-zone modules
  const total = count + margin * 2;
  const moduleSize = px / total;

  canvas.width = px;
  canvas.height = px;

  const ctx = canvas.getContext('2d')!;

  // Background: near-black with a faint radial warmth.
  const bg = ctx.createRadialGradient(px / 2, px / 2, px * 0.1, px / 2, px / 2, px * 0.75);
  bg.addColorStop(0, '#161106');
  bg.addColorStop(1, '#000000');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, px, px);

  // Logo footprint (kept clear of modules so it sits on solid background).
  const logoFrac = 0.22;
  const logoBox = px * logoFrac;
  const logoPad = moduleSize * 0.6;
  const clearBox = logoBox + logoPad * 2;
  const clearX = (px - clearBox) / 2;
  const clearY = (px - clearBox) / 2;

  const inClearZone = (cx: number, cy: number) =>
    cx >= clearX && cx <= clearX + clearBox && cy >= clearY && cy <= clearY + clearBox;

  // Build the path of all dark modules, skipping the logo zone. Modules are
  // kept SQUARE on purpose: rounded modules measurably hurt scan reliability
  // (verified — strict decoders fail on rounded variants), so the "premium"
  // feel comes from the gold gradient + sand texture, not module shape.
  const modPath = new Path2D();
  // Overlap modules by a hair to avoid anti-aliased seams between them.
  const bleed = moduleSize * 0.04;
  for (let row = 0; row < count; row++) {
    for (let col = 0; col < count; col++) {
      if (!data[row * count + col]) continue;
      const x = (col + margin) * moduleSize;
      const y = (row + margin) * moduleSize;
      if (inClearZone(x + moduleSize / 2, y + moduleSize / 2)) continue;
      modPath.rect(x - bleed, y - bleed, moduleSize + bleed * 2, moduleSize + bleed * 2);
    }
  }

  // Fill modules with a continuous gold gradient, clipped to the module path.
  ctx.save();
  ctx.clip(modPath);
  const grad = ctx.createLinearGradient(0, 0, px, px);
  grad.addColorStop(0, PRMD_GOLD.light);
  grad.addColorStop(0.5, PRMD_GOLD.mid);
  grad.addColorStop(1, PRMD_GOLD.deep);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, px, px);

  // Sand texture overlay (soft-light) for the brushed-gold feel.
  const tile = getSandTile();
  const pattern = ctx.createPattern(tile, 'repeat');
  if (pattern) {
    ctx.globalCompositeOperation = 'soft-light';
    ctx.globalAlpha = 0.28;
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, px, px);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }
  ctx.restore();

  // Centre logo: dark pad (keeps the logo zone clear of modules so scanning
  // stays reliable) + a thin gold ring + the transparent PNG.
  const padX = (px - clearBox) / 2;
  const padY = (px - clearBox) / 2;
  ctx.save();
  const pad = new Path2D();
  roundRect(pad, padX, padY, clearBox, clearBox, clearBox * 0.22);
  ctx.fillStyle = '#000000';
  ctx.fill(pad);
  ctx.lineWidth = Math.max(0.75, moduleSize * 0.16);
  ctx.strokeStyle = PRMD_GOLD.mid;
  ctx.stroke(pad);
  ctx.restore();

  if (logoImg) {
    const lx = (px - logoBox) / 2;
    const ly = (px - logoBox) / 2;
    ctx.drawImage(logoImg, lx, ly, logoBox, logoBox);
  }
}

/**
 * Renders the styled QR at an exact `px`×`px` resolution and returns a PNG data
 * URL. Awaits the centre logo so the exported image is always complete — used
 * for downloads that must be print-ready (e.g. 1024px for banners/posters).
 */
export async function renderQrDataUrl(
  value: string,
  px = 1024,
  logoSrc = '/prmd-logo.webp',
): Promise<string> {
  const img = await loadLogo(logoSrc);
  const canvas = document.createElement('canvas');
  paintStyledQr(canvas, value, px, img ?? undefined);
  return canvas.toDataURL('image/png');
}
